import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import React, { useMemo, useCallback, useEffect, forwardRef } from 'react'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { FilledTextField } from '../FilledTextField/FilledTextField'
import { SelectorProps, IGroupOptions } from '../OWAutocomplete'

function SelectorWithInputComponent(props: SelectorProps, ref: any) {
    const {
        label,
        options,
        resetValue = null,
        onChangeCallback,
        hasEmpty,
        emptyLabel = 'None',
        loading = false,
        isGroupLetter = false,
        selectFirstValue = false,
        value,
        disabled,
        helperText,
        error,
        onChange,
        inputProps,
        sortLabel,
        disableClearable = false,
        ...otherProps
    } = props

    const { isMobile } = useScreenSize()
    const Component = isMobile ? FilledTextField : TextField

    const groupOptions: IGroupOptions[] = useMemo(() => {
        if (isGroupLetter)
            return options.map<IGroupOptions>((option) => {
                const groupValue = option.label[0].toUpperCase()
                return {
                    groupValue: /[0-9]/.test(groupValue) ? '0-9' : groupValue,
                    ...option,
                }
            })
        else return options
    }, [isGroupLetter, options])

    const nomalizeOptions = useMemo(() => {
        let results = groupOptions
        if (hasEmpty)
            results = [{ label: emptyLabel, value: '' }, ...groupOptions]
        if (isGroupLetter) {
            results = [
                // support None / Empty Option which have no group
                ...results.filter((i) => !i.groupValue),
                ...results
                    .filter((i) => i.groupValue)
                    .sort(
                        (a, b) => -b.groupValue!.localeCompare(a.groupValue!),
                    ),
            ]
        } else if (sortLabel)
            return results.sort((a, b) => -b.label!.localeCompare(a.label!))
        return results
    }, [emptyLabel, groupOptions, hasEmpty, isGroupLetter, sortLabel])
    const formatSelectValue = useCallback(
        (val: any) => {
            const defaultV = groupOptions.find((f) => f.value === val)
            return defaultV || null
        },
        [groupOptions],
    )
    const onChangeHandle = useCallback(
        (option: IGroupOptions) => {
            onChange?.(option?.value)
            if (onChangeCallback) onChangeCallback(option?.value)
        },
        [onChange, onChangeCallback],
    )

    useEffect(() => {
        if (selectFirstValue && !value && nomalizeOptions.length > 0)
            onChangeHandle(nomalizeOptions[0] ?? null)
    }, [nomalizeOptions, onChangeHandle, selectFirstValue, value])
    return (
        <Autocomplete
            disabled={disabled}
            color="primary"
            options={nomalizeOptions}
            value={formatSelectValue(value)}
            onChange={(_, value) => {
                if (value === null) onChangeHandle(resetValue)
                else onChangeHandle(value)
            }}
            isOptionEqualToValue={(option, value) =>
                option.value === value.value
            }
            groupBy={(option) => option.groupValue ?? ''}
            getOptionLabel={(option) => option.label ?? ''}
            renderInput={(params) => {
                return (
                    <Component
                        {...otherProps}
                        {...params}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? (
                                        <CircularProgress
                                            color="inherit"
                                            size={20}
                                        />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        }}
                        label={label}
                        error={error && !!helperText}
                        helperText={helperText}
                    />
                )
            }}
            disableClearable={disableClearable}
            ref={ref}
        />
    )
}

export const SelectorWithInput = forwardRef<any, SelectorProps>(
    SelectorWithInputComponent,
)
